import { useMemo } from 'react';
import { StanDropdownMenu } from '@/components/core-components/StanDropdown/Menu/StanDropdownMenu';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { FiMoreVertical } from 'react-icons/fi';
import type { DropdownMenuOption } from '@/components/core-components/StanDropdown/StanDropdown.types';

import IcPlus from '@/assets/icons/ic-plus.svg';
import IcBox from '@/assets/icons/ic-box.svg';
import IcPerson from '@/assets/icons/ic-profile.svg';
import IcClose from '@/assets/icons/ic-x-close.svg';
import IcDownload from '@/assets/icons/ic-download.svg';
import IcTrash from '@/assets/icons/ic-trash.svg';
import IcLink from '@/assets/icons/ic-link.svg';

export const CUSTOMER_ACTION_VALUES = {
    ADD_TAG: 'add-tag',
    GRANT_PRODUCT_ACCESS: 'grant-product-access',
    ADD_TO_EMAIL_FLOW: 'add-to-email-flow',
    REMOVE_TAG: 'remove-tag',
    DOWNLOAD_CSV: 'download-csv',
    DELETE_CUSTOMERS: 'delete-customers',
    GRANT_AFFILIATE_ACCESS: 'grant-affiliate-access',
} as const;

export type CustomerActionValue = (typeof CUSTOMER_ACTION_VALUES)[keyof typeof CUSTOMER_ACTION_VALUES];

interface CustomerActionsDropdownProps {
    onActionSelect: (action: CustomerActionValue) => void;
    hasAffiliateProduct?: boolean;
    disabled?: boolean;
}

export function CustomerActionsDropdown({
    onActionSelect,
    hasAffiliateProduct = false,
    disabled = false,
}: CustomerActionsDropdownProps) {
    const dropdownOptions = useMemo((): DropdownMenuOption[] => {
        const options: DropdownMenuOption[] = [
            { label: 'Add Tag', value: CUSTOMER_ACTION_VALUES.ADD_TAG, icon: IcPlus },
            { label: 'Grant Product Access', value: CUSTOMER_ACTION_VALUES.GRANT_PRODUCT_ACCESS, icon: IcBox },
            { label: 'Add To Email Flow', value: CUSTOMER_ACTION_VALUES.ADD_TO_EMAIL_FLOW, icon: IcPerson },
            { label: 'Remove Tag', value: CUSTOMER_ACTION_VALUES.REMOVE_TAG, icon: IcClose },
            { label: 'Download CSV', value: CUSTOMER_ACTION_VALUES.DOWNLOAD_CSV, icon: IcDownload },
            {
                label: 'Delete Customers',
                value: CUSTOMER_ACTION_VALUES.DELETE_CUSTOMERS,
                icon: IcTrash,
                type: 'destructive',
            },
        ];

        // Conditionally add Grant Affiliate Access if store has affiliate products
        if (hasAffiliateProduct) {
            options.push({
                label: 'Grant Affiliate Access',
                value: CUSTOMER_ACTION_VALUES.GRANT_AFFILIATE_ACCESS,
                icon: IcLink,
            });
        }

        return options;
    }, [hasAffiliateProduct]);

    const handleSelect = (value: { value: string }) => {
        onActionSelect(value.value as CustomerActionValue);
    };

    return (
        <DropdownProvider dropdownId='customer-actions' dropdownName='customer-actions'>
            <StanDropdownMenu
                id='customer-actions'
                name='customer-actions'
                options={dropdownOptions}
                placement='bottom-end'
                onSelect={handleSelect}
                currentValue={null}
                minWidth={230}
                disabled={disabled}
            >
                <button
                    className='text-gray hover:text-dark flex size-8 cursor-pointer items-center justify-center rounded-md transition-colors hover:bg-neutral-100'
                    disabled={disabled}
                    aria-label='Customer actions'
                >
                    <FiMoreVertical className='size-5' />
                </button>
            </StanDropdownMenu>
        </DropdownProvider>
    );
}

CustomerActionsDropdown.displayName = 'CustomerActionsDropdown';
